import React, { Component } from "react";
import PropTypes from "prop-types";
import { ClientService, ContactService } from "@services";
import utils from "@utils";
import { Modal } from "../../../../../components";

class JobRequestClientSearch extends Component {
	constructor(props) {
		super(props);

		this.state = {
			showModal: false,
			matchList: [],
			clientHasName: false,
			newClientName: "",
			clientNameError: "",
		};
	}

	componentDidUpdate({ clients: prevClients }) {
		const { clients } = this.props;
		if (prevClients.length !== clients.length && clients.length > 0) {
			this.findClientMatch();
		}
	}

	openModal = config => {
		const { clients } = this.props;
		this.setState(
			{
				showModal: true,
				newClientName: config.clientName,
				clientHasName: config.clientName === "",
			},
			() => {
				if (clients.length > 0) this.findClientMatch();
			},
		);
	};

	/** Attempts to find a match between the provided client name and the list of available clients for the current user */
	findClientMatch = () => {
		const { clients } = this.props;
		const { newClientName } = this.state;
		// Check for null or undefined client name (FE-253)
		const matchList = clients.filter(
			client => client.label?.toLowerCase() === newClientName.toLowerCase(),
		);
		this.setState(prevState => ({
			matchList,
			newClientName: matchList.length === 1 ? matchList[0].label : prevState.newClientName,
		}));
	};

	closeModal = () => {
		const { updateRequestProperties } = this.props;
		this.setState({ showModal: false }, updateRequestProperties({ showClientModal: false }));
	};

	/** Confirms acceptance of the client name provided in the job request. Also handles creating/updating contact record as needed */
	acceptClient = () => {
		const { matchList, newClientName } = this.state;
		const {
			clients,
			getClients,
			getContacts,
			updateClientId,
			jobRequestProperties,
			updateRequestProperties,
		} = this.props;
		const {
			userFirstName: firstName,
			userLastName: lastName,
			streetAddress: address,
			city,
			stateId: state,
			zipCode,
			userPhone: phone,
			userEmail: email,
		} = jobRequestProperties;

		if (matchList.length === 0) {
			// If the client name field is empty, display an appropriate error
			if (newClientName === "") {
				this.setState({ clientNameError: "Please enter a client name" });
				return;
			}
			// If the chosen name is already taken by an existing client, display an error
			if (
				clients.findIndex(
					({ label }) => label.toLowerCase() === newClientName.toLowerCase(),
				) !== -1
			) {
				this.setState({ clientNameError: "A client with that name exists" });
				return;
			}
			// Otherise, we need to create a new client and default project
			ClientService.save({ Name: newClientName }, { createDefaultProject: true })
				.then(({ data: clientData }) => {
					// Get an updated list of clients and update the clientId
					getClients();
					updateClientId({ value: clientData.Id });
					// For a new client, automatically create an associated contact since it is not a required form field
					// but is used to link the Client record to the Organization record
					const newContact = {
						firstName,
						lastName,
						address,
						city,
						state,
						zipCode,
						phone,
						email,
						isAdmin: true,
						clientId: clientData.Id,
					};
					ContactService.save(newContact)
						.then(() => {})
						.catch(error => {
							const {
								response: { data: contactData, status },
							} = error;
							if (status === 409 && contactData?.Data) {
								ContactService.mergeContact({
									id: contactData.Data.Id,
									clientId: clientData.Id,
								}).catch(() =>
									utils.notify({
										type: "error",
										message: "Unable to create contact for this client",
										timeOut: 1500,
									}),
								);
							}
						})
						.finally(() => {
							// Close the modal and turn off the flag to open the modal
							getContacts(clientData.Id);
							this.setState(
								{ showModal: false },
								updateRequestProperties({ showClientModal: false }),
							);
						});
				})
				.catch(() => this.setState({ clientNameError: "Unable to create new client" }));
		} else if (matchList.length === 1) {
			const [{ value }] = matchList;
			updateClientId({ value });
			// Just in case the existing client has no client contacts, create one.
			// If it has a contact, update the record with this job's information
			const newContact = {
				firstName,
				lastName,
				address,
				city,
				state,
				zipCode,
				phone,
				email,
				isAdmin: true,
				clientId: value,
			};
			ContactService.save(newContact)
				.then(() => {})
				.catch(error => {
					const {
						response: { data: contactData, status },
					} = error;
					if (status === 409 && contactData?.Data) {
						ContactService.mergeContact({
							id: contactData.Data.Id,
							clientId: value,
						}).catch(() =>
							utils.notify({
								type: "error",
								message: "Unable to update contact info for this client",
								timeOut: 1500,
							}),
						);
					}
				})
				.finally(() => {
					getContacts(value);
					// Close the modal and turn off the flag to open the modal
					this.setState(
						{ showModal: false },
						updateRequestProperties({ showClientModal: false }),
					);
				});
		}
	};

	handleInputChange = event => {
		const {
			target: { value },
		} = event;
		this.setState(prevState => ({
			clientNameError: prevState.clientNameError !== "" ? "" : prevState.clientNameError,
			newClientName: value,
		}));
	};

	render() {
		const { showModal, matchList, clientHasName, newClientName, clientNameError } = this.state;

		const { onHide } = this.props;

		return (
			<Modal
				show={showModal}
				onHide={onHide}
				backdrop="static"
				id="request-client-search-modal"
			>
				<Modal.Header>
					<Modal.Title>Job Request: Select Client</Modal.Title>
				</Modal.Header>
				<Modal.Body className="row text-center">
					<Choose>
						<When condition={matchList.length <= 1}>
							<Choose>
								<When condition={clientHasName}>
									Please enter a name and press OK to create a new client.
								</When>
								<When condition={!clientHasName && matchList.length === 0}>
									Client not found. Press OK to create a new client with this
									name.
									<br />
									<em className="margin-top-10">
										You can change the client&apos;s name if needed
									</em>
								</When>
								<Otherwise>
									<strong>Please confirm that the client is correct</strong>
									<br />
									<em className="margin-top-10">
										If not, you can close this modal and select/create a
										different client
									</em>
								</Otherwise>
							</Choose>
							<div className="form-group col-sm-6 col-sm-offset-3">
								<input
									type="text"
									id="newClientName"
									placeholder="Enter client name"
									className="margin-top-20 form-control"
									value={newClientName}
									readOnly={matchList.length === 1}
									onChange={e => this.handleInputChange(e)}
								/>
								<span className="text-danger">{clientNameError}</span>
							</div>
						</When>
						<Otherwise>More than one match</Otherwise>
					</Choose>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						className="btn btn-primary"
						id="job-req-create-new-client"
						onClick={() => this.acceptClient()}
					>
						OK
					</button>
					<button
						type="button"
						className="btn btn-link"
						id="job-req-cancel-create-client"
						onClick={this.closeModal}
					>
						Cancel
					</button>
				</Modal.Footer>
			</Modal>
		);
	}
}

JobRequestClientSearch.propTypes = {
	getClients: PropTypes.func.isRequired,
	getContacts: PropTypes.func.isRequired,
	updateClientId: PropTypes.func.isRequired,
	updateRequestProperties: PropTypes.func.isRequired,
	clients: PropTypes.array,
	jobRequestProperties: PropTypes.object,
	onHide: PropTypes.func,
};

JobRequestClientSearch.defaultProps = {
	clients: [],
	jobRequestProperties: {},
	onHide: () => {},
};

export default JobRequestClientSearch;
